// 注册页 — Fluent UI v9
// oauth_only 模式下:本地注册已关闭(后端 403),页面直接重定向到登录页。
// 后端守护:即便绕过 UI,POST /api/v1/auth/register 也会被 403。
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Title3,
} from '@fluentui/react-components';
import { PersonRegular, MailRegular, LockClosedRegular } from '@fluentui/react-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { ROUTES } from '@/routes';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { getAuthMode } from '@/lib/authMode';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-z0-9_-]+$/;

const useStyles = makeStyles({
  fullPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: '380px',
    maxWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
  },
  cardBody: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalL,
    fontWeight: 600,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalXS,
  },
  fieldError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  submitButton: {
    width: '100%',
  },
  footer: {
    marginTop: tokens.spacingVerticalXL,
    textAlign: 'center',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  link: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

type FieldErrors = {
  username?: string;
  email?: string;
  password?: string;
};

export function RegisterPage() {
  const styles = useStyles();
  const { register, loading } = useAuthStore();
  const navigate = useNavigate();

  // oauth_only 模式:本地注册不可用,直接跳到登录页(后端也会 403)
  if (getAuthMode() === 'oauth_only') {
    return <Navigate to={ROUTES.login} replace />;
  }

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  function validate() {
    const e: FieldErrors = {};
    if (!username.trim()) {
      e.username = '请输入用户名';
    } else if (username.length < 3 || username.length > 39) {
      e.username = '3-39 字符';
    } else if (!USERNAME_RE.test(username)) {
      e.username = '只允许小写字母、数字、_、-';
    }

    if (!email.trim()) {
      e.email = '请输入邮箱';
    } else if (!EMAIL_RE.test(email)) {
      e.email = '邮箱格式不正确';
    }

    if (!password) {
      e.password = '请输入密码';
    } else if (password.length < 8) {
      e.password = '至少 8 个字符';
    } else if (password.length > 1024) {
      e.password = '不能超过 1024 字符';
    }

    setErrors(e);
    return !e.username && !e.email && !e.password;
  }

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setError(null);
    setSuccess(null);
    try {
      await register(username.trim(), email.trim(), password);
      setSuccess('注册成功');
      navigate(ROUTES.dashboard, { replace: true });
    } catch (err) {
      const msg = explainError(err);
      console.error(msg);
      setError(msg);
    }
  }

  return (
    <div className={styles.fullPage}>
      <Card className={styles.card}>
        <div className={styles.cardBody}>
          <Title3 as="h1" className={styles.title}>
            注册
          </Title3>

          {error && (
            <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
          {success && (
            <MessageBar intent="success" style={{ marginBottom: tokens.spacingVerticalL }}>
              <MessageBarBody>{success}</MessageBarBody>
            </MessageBar>
          )}

          <form onSubmit={handleRegister} autoComplete="off">
            <div className={styles.field}>
              <Label htmlFor="reg-username" required>
                用户名 <HelpPopover content="登录用的唯一标识,3–39 位,仅限小写字母、数字、下划线(_)与连字符(-)。创建后不可修改。" />
              </Label>
              <Input
                id="reg-username"
                value={username}
                onChange={(_, d) => setUsername(d.value)}
                contentBefore={<PersonRegular />}
                placeholder="alice"
                autoFocus
              />
              {errors.username && (
                <span className={styles.fieldError}>{errors.username}</span>
              )}
            </div>

            <div className={styles.field}>
              <Label htmlFor="reg-email" required>
                邮箱 <HelpPopover content="用于找回密码与接收通知,需为有效的邮箱地址格式。" />
              </Label>
              <Input
                id="reg-email"
                value={email}
                onChange={(_, d) => setEmail(d.value)}
                contentBefore={<MailRegular />}
                placeholder="alice@example.com"
              />
              {errors.email && (
                <span className={styles.fieldError}>{errors.email}</span>
              )}
            </div>

            <div className={styles.field}>
              <Label htmlFor="reg-password" required>
                密码 <HelpPopover content="至少 8 位,最长 1024 位。建议使用大小写字母、数字与符号的组合以提高安全性。" />
              </Label>
              <Input
                id="reg-password"
                type="password"
                value={password}
                onChange={(_, d) => setPassword(d.value)}
                contentBefore={<LockClosedRegular />}
                placeholder="至少 8 个字符"
              />
              {errors.password && (
                <span className={styles.fieldError}>{errors.password}</span>
              )}
            </div>

            <Button
              type="submit"
              appearance="primary"
              disabled={loading}
              icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
              className={styles.submitButton}
            >
              创建账号
            </Button>
          </form>

          <div className={styles.footer}>
            已有账号?{' '}
            <Link to={ROUTES.login} className={styles.link}>
              去登录
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
